import React, { useState } from 'react';
import {
    Input,
    Card,
    Collapse,
    Typography,
    Row,
    Col,
    Button,
    Space,
    Tag,
    Divider,
    Tabs,
} from 'antd';
import {
    SearchOutlined,
    QuestionCircleOutlined,
    PhoneOutlined,
    MailOutlined,
    MessageOutlined,
    ClockCircleOutlined,
    StarOutlined,
    ShoppingCartOutlined,
    CreditCardOutlined,
    TruckOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { Panel } = Collapse;
const { TabPane } = Tabs;

function FAQ() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('general');

    const faqData = {
        general: [
            {
                question: 'What is Spoon & Spice?',
                answer: 'Spoon & Spice is India\'s premier catering platform that connects customers with verified, professional caterers for their special occasions. We provide a seamless booking experience with real-time pricing, quality guarantees, and comprehensive event management services.',
            },
            {
                question: 'How does Spoon & Spice work?',
                answer: 'Simply select your city, occasion, date, and services on our platform. You\'ll get real-time pricing from verified caterers in your area. Choose your preferred vendor, customize your menu, and complete the booking process. We handle everything from payment processing to delivery coordination.',
            },
            {
                question: 'What cities do you serve?',
                answer: 'We currently serve major cities across India including Mumbai, Delhi, Bangalore, Chennai, Hyderabad, Pune, Kolkata, Ahmedabad, Jaipur, and many more. We\'re constantly expanding to new cities. Check our homepage to see if we serve your city.',
            },
            {
                question: 'Are your vendors verified?',
                answer: 'Yes, all our vendors go through a rigorous verification process including license verification, food safety certifications, customer reviews, and quality checks. We regularly audit our vendor partners to ensure they maintain high standards.',
            },
            {
                question: 'What types of events do you cater to?',
                answer: 'We cater to all types of events including weddings, corporate events, birthday parties, anniversaries, festivals, house parties, office celebrations, and any other special occasions. Our vendors specialize in different types of events.',
            },
        ],
        booking: [
            {
                question: 'How far in advance should I book?',
                answer: 'We recommend booking at least 7-14 days in advance for the best vendor availability and pricing. For popular dates, large events, or during peak seasons (wedding season, festivals), booking 3-4 weeks in advance is ideal.',
            },
            {
                question: 'Can I modify my booking after placing it?',
                answer: 'Yes, you can modify your booking up to 48 hours before the event date. Changes to guest count, menu items, or services may affect pricing. Contact our support team for assistance with modifications.',
            },
            {
                question: 'What if I need to cancel my booking?',
                answer: 'Cancellation policies vary by vendor. Generally, cancellations made 48+ hours in advance receive a full refund, 24-48 hours receive 50% refund, and less than 24 hours receive no refund. Check the specific cancellation policy during booking.',
            },
            {
                question: 'How do I track my order status?',
                answer: 'Once your booking is confirmed, you\'ll receive a confirmation email with tracking details. You can also track your order status in your account dashboard. We\'ll send you regular updates about your order progress.',
            },
            {
                question: 'Can I book for multiple dates?',
                answer: 'Yes, you can book for multiple dates. Each booking is treated as a separate order. Some vendors may offer discounts for multiple bookings - check with them directly or contact our support team.',
            },
        ],
        payment: [
            {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit/debit cards (Visa, MasterCard, American Express), UPI, net banking, and digital wallets like Paytm, PhonePe, Google Pay, and Amazon Pay. Cash on delivery is also available for select vendors.',
            },
            {
                question: 'When do I need to pay?',
                answer: 'Payment terms vary by vendor. Some require full payment upfront, while others accept partial payment with the balance due on delivery. Payment terms are clearly displayed during the booking process.',
            },
            {
                question: 'Are there any hidden charges?',
                answer: 'No hidden charges! The price shown during booking includes all applicable taxes and service charges. Any additional fees (like delivery charges or setup fees) will be clearly mentioned before you confirm your booking.',
            },
            {
                question: 'Do you offer refunds?',
                answer: 'Yes, we offer refunds according to our cancellation policy. Refunds are processed within 5-7 business days to your original payment method. The refund amount depends on when you cancel relative to your event date.',
            },
            {
                question: 'Is my payment information secure?',
                answer: 'Yes, we use industry-standard encryption and security measures to protect your payment information. We don\'t store your payment details - all transactions are processed securely through our payment partners.',
            },
        ],
        delivery: [
            {
                question: 'Do you provide delivery service?',
                answer: 'Yes, most of our vendors provide delivery service. Delivery charges and minimum order amounts vary by vendor and location. Check vendor details for specific delivery information and coverage areas.',
            },
            {
                question: 'What if my food arrives late?',
                answer: 'We understand the importance of timely delivery. If your food arrives significantly late, please contact our support team immediately. We\'ll work with the vendor to resolve the issue and may provide compensation.',
            },
            {
                question: 'Do you provide setup and serving staff?',
                answer: 'Many vendors offer setup and serving staff as additional services. You can request these services during booking. Charges for additional staff are clearly mentioned. Some vendors include basic setup in their package.',
            },
            {
                question: 'What if the food quality is not as expected?',
                answer: 'We take food quality seriously. If you\'re not satisfied with the food quality, please contact our support team within 2 hours of delivery. We\'ll investigate and provide appropriate resolution, which may include refunds or replacements.',
            },
            {
                question: 'Can I request specific delivery times?',
                answer: 'Yes, you can specify your preferred delivery time during booking. Vendors will do their best to accommodate your request, though exact timing may depend on their schedule and other orders.',
            },
        ],
        vendor: [
            {
                question: 'How do you verify vendors?',
                answer: 'All vendors go through a rigorous verification process including license verification, food safety certifications, customer reviews, and quality checks. We also conduct regular audits and mystery shopping to ensure they maintain high standards.',
            },
            {
                question: 'Can I see vendor reviews and ratings?',
                answer: 'Yes, you can see detailed reviews and ratings for each vendor on their profile page. Reviews are from verified customers who have used the vendor\'s services. We encourage honest feedback to help other customers make informed decisions.',
            },
            {
                question: 'What if I want to become a vendor?',
                answer: 'We\'re always looking for quality catering partners! Visit our vendor registration page or contact our vendor relations team at vendors@spoonandspice.com. We\'ll guide you through the application and verification process.',
            },
            {
                question: 'How do I report an issue with a vendor?',
                answer: 'You can report issues through our support channels, by calling our customer service number, or through the feedback form on our website. We take all complaints seriously and will investigate promptly.',
            },
            {
                question: 'Do vendors have insurance coverage?',
                answer: 'Yes, all our verified vendors are required to maintain appropriate insurance coverage including general liability and food safety insurance. This provides additional protection for both vendors and customers.',
            },
        ],
    };

    const contactMethods = [
        {
            icon: <PhoneOutlined className="text-2xl text-blue-600" />,
            title: 'Call Us',
            description: 'Speak with our support team',
            contact: '+91 7483423925',
            action: 'Call Now',
            href: 'tel:+917483423925',
        },
        {
            icon: <MailOutlined className="text-2xl text-green-600" />,
            title: 'Email Us',
            description: 'Send us your questions',
            contact: 'support@spoonandspice.com',
            action: 'Send Email',
            href: 'mailto:support@spoonandspice.com',
        },
        {
            icon: <MessageOutlined className="text-2xl text-purple-600" />,
            title: 'Live Chat',
            description: 'Chat with us instantly',
            contact: 'Available 9 AM - 9 PM',
            action: 'Start Chat',
            href: '#',
        },
    ];

    const getFilteredFAQs = (category) => {
        if (!searchTerm) return faqData[category];
        
        return faqData[category].filter(faq =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const tabItems = [
        {
            key: 'general',
            label: (
                <span>
                    <QuestionCircleOutlined />
                    General
                </span>
            ),
        },
        {
            key: 'booking',
            label: (
                <span>
                    <ShoppingCartOutlined />
                    Booking
                </span>
            ),
        },
        {
            key: 'payment',
            label: (
                <span>
                    <CreditCardOutlined />
                    Payment
                </span>
            ),
        },
        {
            key: 'delivery',
            label: (
                <span>
                    <TruckOutlined />
                    Delivery
                </span>
            ),
        },
        {
            key: 'vendor',
            label: (
                <span>
                    <UserOutlined />
                    Vendors
                </span>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <Title level={1} className="!text-white !mb-4">
                            Frequently Asked Questions
                        </Title>
                        <Paragraph className="!text-purple-100 !text-lg !mb-8 max-w-2xl mx-auto">
                            Find quick answers to common questions about our catering services
                        </Paragraph>
                        
                        <div className="max-w-2xl mx-auto">
                            <Search
                                placeholder="Search FAQs..."
                                allowClear
                                size="large"
                                onSearch={handleSearch}
                                onChange={(e) => handleSearch(e.target.value)}
                                prefix={<SearchOutlined />}
                                className="mb-8"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Quick Contact */}
                <Card className="mb-12">
                    <Title level={3} className="!mb-6 text-center">
                        Still Need Help?
                    </Title>
                    <Row gutter={[24, 24]}>
                        {contactMethods.map((method, index) => (
                            <Col xs={24} md={8} key={index}>
                                <div className="text-center p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                    <div className="mb-4">{method.icon}</div>
                                    <Title level={4} className="!mb-2">{method.title}</Title>
                                    <Paragraph className="!mb-2 text-gray-600">{method.description}</Paragraph>
                                    <Text strong className="block mb-4">{method.contact}</Text>
                                    <Button type="primary" href={method.href}>
                                        {method.action}
                                    </Button>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>

                {/* FAQ Tabs */}
                <Card>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        className="faq-tabs"
                    />
                    
                    <div className="mt-6">
                        {tabItems.map((tab) => {
                            const filteredFAQs = getFilteredFAQs(tab.key);
                            
                            return (
                                <div key={tab.key} style={{ display: activeTab === tab.key ? 'block' : 'none' }}>
                                    {filteredFAQs.length > 0 ? (
                                        <Collapse
                                            accordion
                                            size="large"
                                            className="faq-collapse"
                                        >
                                            {filteredFAQs.map((faq, index) => (
                                                <Panel
                                                    header={
                                                        <div className="font-medium text-gray-800">
                                                            {faq.question}
                                                        </div>
                                                    }
                                                    key={`${tab.key}-${index}`}
                                                >
                                                    <Paragraph className="!mb-0 text-gray-700">
                                                        {faq.answer}
                                                    </Paragraph>
                                                </Panel>
                                            ))}
                                        </Collapse>
                                    ) : (
                                        <div className="text-center py-12">
                                            <QuestionCircleOutlined className="text-6xl text-gray-400 mb-4" />
                                            <Title level={3} className="!mb-2">No results found</Title>
                                            <Paragraph className="!text-gray-600 !mb-6">
                                                We couldn't find any FAQs matching your search in this category.
                                            </Paragraph>
                                            <Space>
                                                <Button onClick={() => setSearchTerm('')}>Clear Search</Button>
                                                <Button type="primary" href="/contact">
                                                    Contact Support
                                                </Button>
                                            </Space>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card>

                {/* Additional Resources */}
                <Card className="mt-12">
                    <Title level={3} className="!mb-6 text-center">
                        Additional Resources
                    </Title>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <ClockCircleOutlined className="text-4xl text-blue-600 mb-4" />
                                <Title level={4} className="!mb-2">Business Hours</Title>
                                <Paragraph className="!text-gray-600">
                                    Monday - Friday: 9:00 AM - 9:00 PM<br />
                                    Saturday - Sunday: 10:00 AM - 8:00 PM
                                </Paragraph>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <MessageOutlined className="text-4xl text-green-600 mb-4" />
                                <Title level={4} className="!mb-2">Response Time</Title>
                                <Paragraph className="!text-gray-600">
                                    Email: Within 2 hours<br />
                                    Phone: Immediate<br />
                                    Live Chat: Within 5 minutes
                                </Paragraph>
                            </div>
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="text-center p-6">
                                <StarOutlined className="text-4xl text-purple-600 mb-4" />
                                <Title level={4} className="!mb-2">Customer Satisfaction</Title>
                                <Paragraph className="!text-gray-600">
                                    98% customer satisfaction rate<br />
                                    4.8/5 average vendor rating<br />
                                    24/7 customer support
                                </Paragraph>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Related Links */}
                <Card className="mt-8">
                    <Title level={3} className="!mb-6 text-center">
                        Related Information
                    </Title>
                    <Row gutter={[16, 16]} justify="center">
                        <Col>
                            <Button href="/help-center">Help Center</Button>
                        </Col>
                        <Col>
                            <Button href="/contact">Contact Us</Button>
                        </Col>
                        <Col>
                            <Button href="/privacy-policy">Privacy Policy</Button>
                        </Col>
                        <Col>
                            <Button href="/terms-of-service">Terms of Service</Button>
                        </Col>
                    </Row>
                </Card>
            </div>
        </div>
    );
}

export default FAQ;
